<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/vue/24/outline'
import { useOmnisearchStore } from '@/stores/useOmnisearchStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useOmnisearch, isQueryLongEnough, MIN_QUERY_LENGTH } from '@/v2/composables/useOmnisearch'
import type { OmnisearchEntityKey, TypedSection } from '@/api/omnisearchApi'
import OsSection from './OsSection.vue'
import OsCompanyResult from './results/OsCompanyResult.vue'
import OsContactResult from './results/OsContactResult.vue'
import OsOrderResult from './results/OsOrderResult.vue'
import OsQuoteResult from './results/OsQuoteResult.vue'
import OsInvoiceResult from './results/OsInvoiceResult.vue'
import OsServiceResult from './results/OsServiceResult.vue'
import OsLeadResult from './results/OsLeadResult.vue'
import OsTicketResult from './results/OsTicketResult.vue'
import OsCampaignResult from './results/OsCampaignResult.vue'
import OsEligibilityResult from './results/OsEligibilityResult.vue'
import OsOpportunityResult from './results/OsOpportunityResult.vue'

const store = useOmnisearchStore()
const permissionStore = useBoostCorePermissionStore()
const router = useRouter()
useOmnisearch('overlay')

// L'utilisateur peut accéder à /recherche s'il a le flag OMNISEARCH
const canAccessSearchPage = computed(() => permissionStore.hasFlag('OMNISEARCH'))

const inputRef = ref<HTMLInputElement | null>(null)

// Focus input quand l'overlay s'ouvre
watch(
  () => store.isOpen,
  async (open) => {
    if (open) {
      await nextTick()
      inputRef.value?.focus()
    }
  },
)

// Fermer avec Escape
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') store.close()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

function onInput(e: Event) {
  store.setQuery((e.target as HTMLInputElement).value)
}

function clearQuery() {
  store.setQuery('')
  inputRef.value?.focus()
}

function goToSearchPage() {
  const q = store.query  // capturer AVANT close() qui vide le store
  store.pushHistory(q)
  store.close()
  router.push({ path: '/recherche', query: { q } })
}

function onNavigate() {
  store.pushHistory(store.query)
  store.close()
}

// Drill-down dédié par entité : certaines sections renvoient vers leur page liste
// V2 (recherche dédiée + pagination) plutôt que vers la recherche globale.
// Ces pages ne sont pas gardées par le flag OMNISEARCH → accessibles à tous.
function drilldownPath(key: OmnisearchEntityKey): string | undefined {
  if (key === 'companies') return '/clients'
  if (key === 'contacts') return '/contacts'
  return undefined
}

/** « Voir tout » visible dès que la catégorie est plafonnée (= il y a probablement plus). */
function showAllFor(section: TypedSection): boolean {
  if (section.items.length < 5) return false
  return drilldownPath(section.key) ? true : canAccessSearchPage.value
}

function onShowAll(key: OmnisearchEntityKey) {
  const path = drilldownPath(key)
  if (!path) {
    goToSearchPage()
    return
  }
  const q = store.query // capturer AVANT close() qui vide le store
  store.pushHistory(q)
  store.close()
  router.push({ path, query: { q } })
}

function useRecent(term: string) {
  store.setQuery(term)
  inputRef.value?.focus()
}

// Sections à afficher (seulement celles avec des résultats)
const sections = computed((): TypedSection[] => {
  const r = store.results
  if (!r) return []
  // Tickets visible pour tous : GET /tickets est scopé côté back sur le demandeur courant
  const all: TypedSection[] = [
    { key: 'companies',     label: 'Entreprises',  items: r.companies     },
    { key: 'contacts',      label: 'Contacts',     items: r.contacts      },
    { key: 'orders',        label: 'Commandes',    items: r.orders        },
    { key: 'quotes',        label: 'Devis',        items: r.quotes        },
    { key: 'leads',         label: 'Leads',        items: r.leads         },
    { key: 'tickets',       label: 'Tickets',      items: r.tickets       },
    { key: 'invoices',      label: 'Factures',     items: r.invoices      },
    { key: 'services',      label: 'Services',     items: r.services      },
    { key: 'campaigns',     label: 'Campagnes',    items: r.campaigns     },
    { key: 'eligibilities', label: 'Éligibilités', items: r.eligibilities },
    { key: 'opportunities', label: 'Affaires',     items: r.opportunities },
  ]
  return all.filter((s) => s.items.length > 0)
})

const showEmpty = computed(
  () =>
    isQueryLongEnough(store.query) &&
    !store.isLoading &&
    !store.hasResults &&
    !store.hasError,
)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="store.isOpen"
        class="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4"
        @click.self="store.close()"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="store.close()" />

        <!-- Palette -->
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="store.isOpen"
            class="relative w-full max-w-2xl bg-base-100 rounded-2xl shadow-2xl ring-1 ring-base-300 overflow-hidden flex flex-col max-h-[70vh]"
          >
            <!-- Input bar -->
            <div class="flex items-center gap-3 px-4 py-3 border-b border-base-200">
              <MagnifyingGlassIcon
                class="w-5 h-5 flex-shrink-0 transition-colors"
                :class="store.isLoading ? 'text-primary' : 'text-base-content/40'"
              />
              <input
                ref="inputRef"
                type="text"
                placeholder="Rechercher une entreprise, contact, commande…"
                class="flex-1 bg-transparent outline-none text-base placeholder:text-base-content/30 text-base-content"
                :value="store.query"
                @input="onInput"
                @keydown.enter="canAccessSearchPage && goToSearchPage()"
              />
              <div class="flex items-center gap-2 flex-shrink-0">
                <ArrowPathIcon
                  v-if="store.isLoading"
                  class="w-4 h-4 text-primary animate-spin"
                />
                <button
                  v-if="store.query"
                  class="w-5 h-5 rounded-full flex items-center justify-center hover:bg-base-200 text-base-content/40 hover:text-base-content transition-colors"
                  @click="clearQuery"
                >
                  <XMarkIcon class="w-3.5 h-3.5" />
                </button>
                <kbd class="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono bg-base-200 text-base-content/50 rounded border border-base-300">
                  Esc
                </kbd>
              </div>
            </div>

            <!-- Résultats -->
            <div class="overflow-y-auto flex-1 py-2">

              <!-- Historique (quand pas de requête) -->
              <template v-if="!store.query && store.recentSearches.length">
                <div class="px-3 pt-2 pb-1 flex items-center justify-between">
                  <span class="text-xs font-semibold uppercase tracking-wider text-base-content/50 flex items-center gap-1.5">
                    <ClockIcon class="w-3.5 h-3.5" />
                    Recherches récentes
                  </span>
                  <button class="text-xs text-base-content/40 hover:text-base-content" @click="store.clearHistory()">
                    Effacer
                  </button>
                </div>
                <div class="px-2 py-1 flex flex-wrap gap-2">
                  <button
                    v-for="term in store.recentSearches"
                    :key="term"
                    class="px-3 py-1 text-sm bg-base-200 hover:bg-base-300 rounded-full transition-colors text-base-content/70"
                    @click="useRecent(term)"
                  >
                    {{ term }}
                  </button>
                </div>
              </template>

              <!-- Indice min caractères -->
              <div
                v-else-if="store.query && !isQueryLongEnough(store.query)"
                class="py-8 text-center text-sm text-base-content/40"
              >
                Saisissez au moins {{ MIN_QUERY_LENGTH }} caractères…
              </div>

              <!-- Loader -->
              <div v-else-if="store.isLoading" class="py-10 flex flex-col items-center gap-3 text-base-content/40">
                <ArrowPathIcon class="w-6 h-6 animate-spin text-primary" />
                <span class="text-sm">Recherche en cours…</span>
              </div>

              <!-- Erreur -->
              <div v-else-if="store.hasError" class="py-8 text-center text-sm text-error">
                Une erreur est survenue. Réessayez.
              </div>

              <!-- Aucun résultat -->
              <div v-else-if="showEmpty" class="py-10 text-center">
                <MagnifyingGlassIcon class="w-8 h-8 mx-auto text-base-content/20 mb-2" />
                <p class="text-sm text-base-content/50">Aucun résultat pour <strong>« {{ store.query }} »</strong></p>
              </div>

              <!-- Résultats par section -->
              <template v-else-if="store.hasResults">
                <template v-for="section in sections" :key="section.key">
                  <OsSection
                    :label="section.label"
                    :count="section.items.length"
                    :show-all="showAllFor(section)"
                    @show-all="onShowAll(section.key)"
                  />
                  <template v-if="section.key === 'companies'">
                    <OsCompanyResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'contacts'">
                    <OsContactResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'orders'">
                    <OsOrderResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'quotes'">
                    <OsQuoteResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                    />
                  </template>
                  <template v-else-if="section.key === 'invoices'">
                    <OsInvoiceResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                    />
                  </template>
                  <template v-else-if="section.key === 'services'">
                    <OsServiceResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                    />
                  </template>
                  <template v-else-if="section.key === 'leads'">
                    <OsLeadResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'tickets'">
                    <OsTicketResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'campaigns'">
                    <OsCampaignResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'eligibilities'">
                    <OsEligibilityResult
                      v-for="item in section.items"
                      :key="item.uuid"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                  <template v-else-if="section.key === 'opportunities'">
                    <OsOpportunityResult
                      v-for="item in section.items"
                      :key="item.id"
                      :item="item"
                      :query="store.query"
                      @navigate="onNavigate"
                    />
                  </template>
                </template>

                <!-- Footer: Voir tous les résultats -->
                <div class="border-t border-base-200 mt-2 px-4 py-2.5 flex items-center justify-between">
                  <span class="text-xs text-base-content/40">
                    {{ store.totalResults }} résultat{{ store.totalResults > 1 ? 's' : '' }}
                    <template v-if="canAccessSearchPage">
                      — <kbd class="font-mono">↵</kbd> pour rechercher en détail
                    </template>
                  </span>
                  <button
                    v-if="canAccessSearchPage"
                    class="text-xs text-primary hover:underline font-medium"
                    @click="goToSearchPage"
                  >
                    Voir tous les résultats →
                  </button>
                </div>
              </template>

            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
